import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";
import i18n from "../../../i18n";
import Weekday, { getLabeledWeekdayForWeekday } from "../../../utils/weekday";
import TimeSpan, { getDateTimeDecimal, getStringFromTimeSpan, getTimeSpanDecimalHours, getTimeSpanFromString } from "../../../utils/time-span";
import ValidationResult from "../../../interfaces/api/validation-result";
import RoomData from "../rooms/room-data";
import Mission from "../missions/mission";

interface ScheduleEntry {
  id: string;
  weekdayFrom: Weekday;
  weekdayTo: Weekday;
  timeFrom: TimeSpan;
  timeTo: TimeSpan;
  enabled: boolean;
  missionId: string;
}

export interface ScheduleEntryState {
  scheduleEntry: ScheduleEntry;
  isTemporary: boolean;
  hasBeenEdited: boolean;
  isOverlapping: boolean;
  wasDragged: boolean;
}

export interface ScheduleEntryApi {
  id: string;
  weekdayFrom: string;
  weekdayTo: string;
  timeFrom: string;
  timeTo: string;
  enabled: boolean;
  missionId: string;
}

export const mapScheduleEntryApiToScheduleEntry = (apiData: ScheduleEntryApi) =>
  ({
    ...apiData,
    timeFrom: getTimeSpanFromString(apiData.timeFrom),
    timeTo: getTimeSpanFromString(apiData.timeTo),
  } as ScheduleEntry);

export const mapScheduleEntryToScheduleEntryApi = (scheduleEntry: ScheduleEntry): ScheduleEntryApi => {
  const scheduleEntryApi: ScheduleEntryApi = {
    ...scheduleEntry,
    timeFrom: getStringFromTimeSpan(scheduleEntry.timeFrom),
    timeTo: getStringFromTimeSpan(scheduleEntry.timeTo)
  };

  return scheduleEntryApi;
};

export const mapPartialScheduleEntryToApi = (scheduleEntry: Partial<ScheduleEntry>) =>
  ({
    ...scheduleEntry,
    timeFrom: scheduleEntry.timeFrom !== undefined ? getStringFromTimeSpan(scheduleEntry.timeFrom) : undefined,
    timeTo: scheduleEntry.timeTo !== undefined ? getStringFromTimeSpan(scheduleEntry.timeTo) : undefined
  } as Partial<ScheduleEntryApi>);

export const mapScheduleEntryToScheduleEntryState = (apiData: ScheduleEntry) =>
  ({
    scheduleEntry: apiData,
    isTemporary: false,
    hasBeenEdited: false,
    isOverlapping: false,
    wasDragged: false,
  } as ScheduleEntryState);

export interface ScheduleEntryTimes {
  weekdayFrom: Weekday;
  weekdayTo: Weekday;
  timeFrom: TimeSpan;
  timeTo: TimeSpan;
}

export const getNewScheduleEntry = (
  weekdayFrom: Weekday,
  weekdayTo: Weekday,
  timeFrom: TimeSpan,
  timeTo: TimeSpan,
  enabled: boolean,
  missionId?: string,
  floorId?: string
): ScheduleEntry => {
  return {
    id: uuidv4(),
    weekdayFrom: weekdayFrom,
    weekdayTo: weekdayTo,
    timeFrom: timeFrom,
    timeTo: timeTo,
    enabled: enabled,
    missionId: missionId ?? "",
  };
};

export default ScheduleEntry;

interface ScheduleBox {
  timeFrom: TimeSpan;
  timeTo: TimeSpan;
  weekday: Weekday;
}

export const getScheduleTimesAndDays = (
  timeFrom: TimeSpan,
  weekdayFrom: Weekday,
  timeTo: TimeSpan,
  weekdayTo: Weekday,
): ScheduleBox[] => {
  if (weekdayFrom === weekdayTo) {
    return [
      {
        weekday: weekdayFrom,
        timeFrom: timeFrom,
        timeTo: timeTo,
      },
    ];
  }

  return [
    {
      weekday: weekdayFrom,
      timeFrom: timeFrom,
      timeTo: { hours: 24, minutes: 0 },
    },
    {
      weekday: weekdayTo,
      timeFrom: { hours: 0, minutes: 0 },
      timeTo: timeTo,
    },
  ];
};

type NormalizedInterval = {
  weekday: Weekday;
  start: number;
  end: number;
};

const normalizeSchedule = (schedule: ScheduleEntry): NormalizedInterval[] => {
  const start = getTimeSpanDecimalHours(schedule.timeFrom);
  const end = getTimeSpanDecimalHours(schedule.timeTo);

  // Same day
  if (schedule.weekdayFrom === schedule.weekdayTo) {
    return [
      {
        weekday: schedule.weekdayFrom,
        start,
        end,
      },
    ];
  }

  // Crosses midnight (max 24h assumption)
  return [
    {
      weekday: schedule.weekdayFrom,
      start,
      end: 24,
    },
    {
      weekday: schedule.weekdayTo,
      start: 0,
      end,
    },
  ];
};;

export const isCurrentTimeScheduleEntry = (scheduleEntry: ScheduleEntry) => {
  const currentWeekdayIndex = DateTime.now().weekday;
  const weekdayFromLabeled = getLabeledWeekdayForWeekday(scheduleEntry.weekdayFrom);
  const weekdayToLabeled = getLabeledWeekdayForWeekday(scheduleEntry.weekdayTo);
  if (weekdayFromLabeled == null || weekdayToLabeled == null) {
    return null;
  }

  const timeFromDecimalHours = weekdayFromLabeled.index * 24 + getTimeSpanDecimalHours(scheduleEntry.timeFrom);
  const timeToDecimalHours = weekdayToLabeled.index * 24 + getTimeSpanDecimalHours(scheduleEntry.timeTo);
  const currentTimeDecimalHours = currentWeekdayIndex * 24 + DateTime.now().hour + DateTime.now().minute / 60;

  return (
    // not over Sunday
    (timeToDecimalHours > timeFromDecimalHours &&
      timeFromDecimalHours <= currentTimeDecimalHours &&
      currentTimeDecimalHours <= timeToDecimalHours) ||
    // over Sunday
    (timeToDecimalHours < timeFromDecimalHours &&
      (timeFromDecimalHours <= currentTimeDecimalHours || timeToDecimalHours >= currentTimeDecimalHours))
  );
};

export const copyScheduleEntry = (scheduleEntry: ScheduleEntry) => {
  return {
    ...scheduleEntry,
    timeFrom: { ...scheduleEntry.timeFrom },
    timeTo: { ...scheduleEntry.timeTo },
  } as ScheduleEntry;
};

export const getOverlapPercent = (schedule: ScheduleEntry, startTimeDecimal: number, endTimeDecimal: number) => {
  const fullWeekTimeDecimal = getDateTimeDecimal({ hours: 24, minutes: 0 }, Weekday.Sunday);
  let scheduleStartTimeDecimal = getDateTimeDecimal(schedule.timeFrom, schedule.weekdayFrom);
  let scheduleEndTimeDecimal = getDateTimeDecimal(schedule.timeTo, schedule.weekdayTo);

  if (scheduleStartTimeDecimal > scheduleEndTimeDecimal) {
    scheduleEndTimeDecimal += fullWeekTimeDecimal;
  }

  if (startTimeDecimal > endTimeDecimal) {
    if (scheduleStartTimeDecimal < endTimeDecimal) {
      scheduleStartTimeDecimal += fullWeekTimeDecimal;
      scheduleEndTimeDecimal += fullWeekTimeDecimal;
    }
    endTimeDecimal += fullWeekTimeDecimal;
  }

  const scheduleLength = scheduleEndTimeDecimal - scheduleStartTimeDecimal;

  const overlapStart = Math.max(scheduleStartTimeDecimal, startTimeDecimal);
  const overlapEnd = Math.min(scheduleEndTimeDecimal, endTimeDecimal);

  const overlapLength = Math.max(0, overlapEnd - overlapStart);

  if (scheduleLength !== 0) {
    return (overlapLength / scheduleLength) * 100;
  }
  return 0;
};

export const getEntryScheduleTimeFromScheduledTimes = (scheduleEntry: ScheduleEntry, scheduleEntryTimesArray: ScheduleEntryTimes[]) => {
  return scheduleEntryTimesArray.find(entryTime =>
    scheduleEntry.weekdayFrom === entryTime.weekdayFrom &&
    scheduleEntry.weekdayTo === entryTime.weekdayTo &&
    scheduleEntry.timeFrom.hours === entryTime.timeFrom.hours &&
    scheduleEntry.timeFrom.minutes === entryTime.timeFrom.minutes &&
    scheduleEntry.timeTo.hours === entryTime.timeTo.hours &&
    scheduleEntry.timeTo.minutes === entryTime.timeTo.minutes
  );
};

export const isScheduleInFloor = (
  scheduleEntry: ScheduleEntry,
  floorId: string,
  missions: Mission[],
  rooms: RoomData[],
): boolean => {
  const mission = missions.find((m) => m.id === scheduleEntry.missionId);
  if (!mission) return false;

  const room = rooms.find((r) => r.id === mission.room);
  if (!room) return false;

  return room.floorId === floorId;
};

export const validateSchedulesDontOverlap = (
  scheduleEntries: ScheduleEntry[],
  validatingEntry: ScheduleEntry
): ValidationResult => {
  const validatingEntryBoxTimes = normalizeSchedule(validatingEntry);

  for (let i = 0; i < scheduleEntries.length; i++) {
    const other = scheduleEntries[i];

    if (other.id === validatingEntry.id) continue;

    const otherTimes = normalizeSchedule(other);

    for (let v = 0; v < validatingEntryBoxTimes.length; v++) {
      const vt = validatingEntryBoxTimes[v];

      for (let t = 0; t < otherTimes.length; t++) {
        const ot = otherTimes[t];

        if (vt.weekday !== ot.weekday) continue;

        if (vt.start < ot.end && vt.end > ot.start) {
          return {
            isValid: false,
            error: i18n.t("schedule-validationerror-overlapping-schedules"),
          };
        }
      }
    }
  }
  return { isValid: true, error: null };
};

export const validateScheduleIsNotTooLong = (
  validatingEntry: ScheduleEntry
): ValidationResult => {

  let isValid = true;

  const start = getDateTimeDecimal(validatingEntry.timeFrom, validatingEntry.weekdayFrom);
  const end = getDateTimeDecimal(validatingEntry.timeTo, validatingEntry.weekdayTo);

  let duration: number;

  if (end >= start) {
    duration = end - start;
  } else {
    duration = (7 * 24 - start) + end;
  }

  if (duration > 24 || duration === 0) {
    isValid = false;
  }

  return { isValid: isValid, error: isValid ? null : i18n.t("schedule-validationerror-schedule-too-long") };
};